import { decimalToPercent } from '@calculations/utilities/decimal-places';
import { Box } from '@chakra-ui/react';
import { appendDisplayMagnitudeDescription } from '@lib/util/display-magnitude';
import { DisplayMagnitude } from '@prisma/client';
import { Field } from '@ui/components/Field';
import { DollarSignIcon, HashtagIcon, PercentIcon } from '@ui/components/Icons';
import { useQuoteLineContext } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineProvider';
import { selectDecimalPlaces } from '@ui/features/quote-builder/state/selectors/decimal-places';
import { selectQuoteLineWithLeadTimeIssues } from '@ui/features/quote-builder/state/selectors/quote-line';
import { useCurrentSiteConfig } from '@ui/hooks/useCurrentSiteConfig';
import { useSelector } from '@ui/hooks/useSelector';
import { currency } from '@ui/util/currency';

export function LastWinQuantity() {
  const { quotingConfig } = useCurrentSiteConfig();
  const { quoteLineUuid } = useQuoteLineContext();
  const lastWin = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).lastWin,
  );

  return (
    <Field
      label={appendDisplayMagnitudeDescription(
        'Quantity',
        quotingConfig.quantityDisplayMagnitude ?? DisplayMagnitude.ONES,
      )}
      leftIcon={HashtagIcon}
      isDisabled
      value={lastWin?.quantity?.toString()}
    />
  );
}

export function LastWinUnitCost() {
  const { quoteLineUuid } = useQuoteLineContext();
  const lastWin = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).lastWin,
  );
  const decimalPlaces = useSelector(selectDecimalPlaces);

  return (
    <Field
      label="Unit Cost"
      leftIcon={DollarSignIcon}
      isDisabled
      value={lastWin?.unitCost?.toFixed(decimalPlaces)}
    />
  );
}

export function LastWinUnitVariableCosts() {
  const { quoteLineUuid } = useQuoteLineContext();
  const lastWin = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).lastWin,
  );
  const decimalPlaces = useSelector(selectDecimalPlaces);

  return (
    <>
      <Field
        label="Unit Variable Costs"
        leftIcon={DollarSignIcon}
        isDisabled
        value={lastWin?.unitVariableCosts?.toFixed(decimalPlaces)}
      >
        <Box fontSize="sm" display="flex" px={2} justifyContent="space-between">
          <Box display="flex" flexDirection="column">
            <strong>Labor</strong>
            {currency(lastWin?.unitLaborCost)}
          </Box>
          <Box display="flex" flexDirection="column">
            <strong>Material</strong>
            {currency(lastWin?.unitMaterialCost)}
          </Box>
          <Box display="flex" flexDirection="column">
            <strong>Service</strong>
            {currency(lastWin?.unitServiceCost)}
          </Box>
        </Box>
      </Field>
    </>
  );
}

export function LastWinUnitPrice() {
  const { quoteLineUuid } = useQuoteLineContext();
  const lastWin = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).lastWin,
  );
  const decimalPlaces = useSelector(selectDecimalPlaces);

  return (
    <Field
      label="Unit Price"
      leftIcon={DollarSignIcon}
      isDisabled
      value={lastWin?.unitPrice?.toFixed(decimalPlaces)}
    />
  );
}

export function LastWinContributionMargin() {
  const { quoteLineUuid } = useQuoteLineContext();
  const lastWin = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).lastWin,
  );
  const decimalPlaces = useSelector(selectDecimalPlaces);

  let value = undefined;

  if (lastWin?.contributionMargin) {
    value = decimalToPercent(lastWin.contributionMargin, decimalPlaces);
  }

  return (
    <Field
      label="Contribution Margin"
      leftIcon={PercentIcon}
      isDisabled
      value={value}
    />
  );
}

export function LastWinGrossMargin() {
  const { quoteLineUuid } = useQuoteLineContext();
  const lastWin = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).lastWin,
  );
  const decimalPlaces = useSelector(selectDecimalPlaces);

  let value = undefined;

  if (lastWin?.grossMargin) {
    value = decimalToPercent(lastWin.grossMargin, decimalPlaces);
  }

  return (
    <Field
      label="Gross Margin"
      leftIcon={PercentIcon}
      isDisabled
      value={value}
    />
  );
}
